import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ADMIN_PLANS,
  planLabel,
  toIsoDate,
  type AppUsageAnalytics,
  type TokenCounts,
  type UsageAnalytics,
  type UsageEndpoint,
} from "@currencycore/shared";
import { getTokenUsage, getUsage } from "../lib/api.ts";
import { Select } from "../components/Select.tsx";
import { AreaChart, DonutChart, HBars, PLAN_COLORS, compact } from "../components/Charts.tsx";
import { useConfirm } from "../components/useConfirm.tsx";
import { toast } from "../components/Toast.tsx";
import { errMsg } from "../lib/ui.ts";

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];

const GRANULARITY = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

const PLAN_OPTIONS = [
  { value: "", label: "All plans" },
  ...ADMIN_PLANS.map((p) => ({ value: p, label: planLabel(p) })),
];

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function shortLabel(bucket: string, g: string): string {
  if (g === "day") return bucket.slice(5); // MM-DD
  return bucket; // week / month already short
}

export function UsagePage() {
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState("day");
  const [plan, setPlan] = useState("");
  const [data, setData] = useState<UsageAnalytics | null>(null);
  const [tokens, setTokens] = useState<AppUsageAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { ask, dialog } = useConfirm();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const to = toIsoDate();
    const from = addDays(to, -(days - 1));
    getUsage({ from, to, granularity, plan: plan || undefined })
      .then((d) => alive && setData(d))
      .catch((e) => alive && toast(errMsg(e, "Failed to load usage.")))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [days, granularity, plan]);

  // App-wide AI token analytics (independent of the day-range filters above).
  useEffect(() => {
    let alive = true;
    getTokenUsage()
      .then((t) => alive && setTokens(t))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const overData = (data?.overTime ?? []).map((p) => ({
    label: shortLabel(p.bucket, granularity),
    value: p.requests,
  }));
  const donutSlices =
    data?.byPlan
      .map((s) => ({
        key: s.key,
        label: s.label,
        value: s.points.reduce((a, b) => a + b.requests, 0),
        color: PLAN_COLORS[s.key] ?? "oklch(70% 0.02 260)",
      }))
      .filter((s) => s.value > 0) ?? [];

  function openOrg(id: string) {
    const org = data?.topOrgs.find((o) => o.organizationId === id);
    ask({
      title: "Open organization?",
      message: (
        <>
          Go to <strong>{org?.name ?? id}</strong>'s detail page?
        </>
      ),
      confirmLabel: "Open",
      run: () => navigate(`/organizations/${id}`),
    });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Usage analytics</h1>
          <p className="muted">Platform-wide request volume across organizations.</p>
        </div>
        <div className="page-head-actions usage-controls">
          <div className="segmented">
            {RANGES.map((r) => (
              <button
                key={r.days}
                className={days === r.days ? "active" : ""}
                onClick={() => setDays(r.days)}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Select value={granularity} options={GRANULARITY} onChange={setGranularity} ariaLabel="Granularity" />
          <Select value={plan} options={PLAN_OPTIONS} onChange={setPlan} ariaLabel="Plan filter" />
        </div>
      </div>

      {/* Headline stats */}
      <div className="grid grid-4 stat-grid">
        <div className="card statbox">
          <span className="stat-label">Total requests</span>
          <span className="stat-value">{data ? compact(data.totals.requests) : "—"}</span>
          <span className="stat-sub muted">{data ? data.totals.requests.toLocaleString("en-US") : ""}</span>
        </div>
        <div className="card statbox">
          <span className="stat-label">AI requests</span>
          <span className="stat-value">{data ? compact(data.totals.aiRequests) : "—"}</span>
          <span className="stat-sub muted">{data ? data.totals.aiRequests.toLocaleString("en-US") : ""}</span>
        </div>
        <div className="card statbox">
          <span className="stat-label">Active orgs</span>
          <span className="stat-value">{data ? data.totals.activeOrgs : "—"}</span>
          <span className="stat-sub muted">with traffic in range</span>
        </div>
        <div className="card statbox">
          <span className="stat-label">Growth</span>
          <span className={`stat-value ${growthClass(data?.totals.growthPct)}`}>
            {data?.totals.growthPct == null ? "—" : `${data.totals.growthPct >= 0 ? "+" : ""}${data.totals.growthPct.toFixed(1)}%`}
          </span>
          <span className="stat-sub muted">vs. previous period</span>
        </div>
      </div>

      {/* Over time */}
      <div className="card card-pad chart-card">
        <div className="section-row between">
          <h3>Requests over time</h3>
          {loading && <span className="spinner" />}
        </div>
        <AreaChart data={overData} height={240} />
      </div>

      {/* By plan + top orgs */}
      <div className="grid grid-2 chart-row">
        <div className="card card-pad chart-card">
          <h3>By plan</h3>
          <DonutChart slices={donutSlices} />
        </div>
        <div className="card card-pad chart-card">
          <h3>Top organizations</h3>
          <HBars
            items={(data?.topOrgs ?? []).map((o) => ({ id: o.organizationId, label: o.name, value: o.requests }))}
            onSelect={openOrg}
          />
        </div>
      </div>

      {/* ----- AI tokens (app-wide; input / output / total on every card) ----- */}
      <div className="section-row between" style={{ marginTop: "28px" }}>
        <h2 style={{ margin: 0 }}>AI tokens</h2>
        <span className="muted">{tokens ? `${tokens.monthsActive} active month(s)` : ""}</span>
      </div>
      <div className="grid grid-3 stat-grid">
        <TokenCard label="Total (all time)" t={tokens?.totalTokens} />
        <TokenCard label="This month" t={tokens?.monthTokens} />
        <TokenCard label="Avg per month" t={tokens?.avgMonthlyTokens} />
      </div>

      <div className="grid grid-2 chart-row">
        <div className="card card-pad chart-card">
          <h3>Tokens per month</h3>
          <AreaChart
            data={(tokens?.monthly ?? []).map((m) => ({ label: m.period.slice(2), value: m.totalTokens }))}
            height={220}
          />
        </div>
        <div className="card card-pad chart-card">
          <h3>Endpoint usage (requests)</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th style={{ textAlign: "right" }}>This month</th>
                <th style={{ textAlign: "right" }}>All time</th>
              </tr>
            </thead>
            <tbody>
              {(["convert", "rates", "ppp", "ai"] as UsageEndpoint[]).map((ep) => (
                <tr key={ep}>
                  <td className="mono">{ep}</td>
                  <td style={{ textAlign: "right" }}>{tokens ? tokens.endpointMonth[ep].toLocaleString("en-US") : "—"}</td>
                  <td style={{ textAlign: "right" }}>{tokens ? tokens.endpointTotal[ep].toLocaleString("en-US") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card card-pad chart-card">
        <h3>Top organizations by AI tokens (this month)</h3>
        {tokens && tokens.topOrgs.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th style={{ textAlign: "right" }}>This month (in / out / total)</th>
                <th style={{ textAlign: "right" }}>All-time total</th>
              </tr>
            </thead>
            <tbody>
              {tokens.topOrgs.map((o) => (
                <tr
                  key={o.organizationId}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/organizations/${o.organizationId}`)}
                >
                  <td className="strong">{o.name}</td>
                  <td style={{ textAlign: "right" }}>
                    {compact(o.month.inputTokens)} / {compact(o.month.outputTokens)} /{" "}
                    <strong>{compact(o.month.totalTokens)}</strong>
                  </td>
                  <td style={{ textAlign: "right" }}>{compact(o.total.totalTokens)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">No AI token usage this month yet.</p>
        )}
      </div>
      {dialog}
    </>
  );
}

/** A token stat card showing input / output / total (the standard admin token card). */
function TokenCard({ label, t }: { label: string; t?: TokenCounts }) {
  return (
    <div className="card statbox">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{t ? compact(t.totalTokens) : "—"} total</span>
      <span className="stat-sub muted">
        {t ? `in ${compact(t.inputTokens)} · out ${compact(t.outputTokens)}` : ""}
      </span>
    </div>
  );
}

function growthClass(pct: number | null | undefined): string {
  if (pct == null) return "";
  return pct >= 0 ? "trend-up" : "trend-down";
}
